import { newFeatureFilter, newPropertyFilter } from '../request-filters';
import { Region, getNextChildLocationType } from '../../locations';
import { newBoundingBoxEvent } from '../bounding-box';
import { wrapUpdateCall } from '../api';
import { District, Entity, Island, Municipality, Neighbourhood, Province } from '../../locations';
import { Property } from '../layers';
import { whenFeaturesAvailable } from '../features';
import { removeApproxLocationCircle, removeLayersForSource } from '../layers';
import { removeRadiusState } from '../radius';

import LocationFeature from '../models/feature';

const handleProvinceLayerClick = async ( ctx, event ) => {
    if ( ctx.abortController ) {
        ctx.abortController.abort();
        ctx.abortController = new AbortController();
    }

    const mapFeature = event.detail.feature;

    if ( !mapFeature ) {
        return;
    }

    const feature = LocationFeature.fromMapBoxFeature( mapFeature );

    ctx.setLoading( true );

    ctx.movefuncs = [];

    removeRadiusState( ctx.map );

    removeApproxLocationCircle( ctx.map );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', { bubbles: true } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:remove-area', {
        bubbles: true,
        detail: {
            search: false,
        },
    } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:search-this-area:deactivate', { bubbles: true } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:deactivate', {
        bubbles: true,
        detail: {
            search: false,
        },
    } ) );

    ctx.locationIndicator.update( feature.label( ctx.language ), {
        autohide: true,
        timeout: 5000,
    } );

    let fitBounds = false;

    let currentZoom = ctx.map.getZoom();

    if ( currentZoom < 7 ) {
        fitBounds = true;
    }

    if ( fitBounds && ctx.options.fitBoundsOnClick ) {
        let bboxOptions = {};
        ctx.map.fitBounds( feature.bbox, bboxOptions, newBoundingBoxEvent() );
    }

    if ( ctx.currentLocation !== null ) {
        ctx.previousLocation = ctx.currentLocation;
    }

    ctx.currentLocation = mapFeature;

    const pfilter = [
        '!', [
            'match',
            [ 'get', 'id' ],
            [ feature.id ],
            true,
            false,
        ],
    ];

    if ( ctx.map.getLayer( Province + ':fill' ) ) {
        ctx.map.setFilter( Province + ':fill', pfilter, {
            validate: ctx.debug,
        } );

        ctx.map.setFilter( Province + ':outline', pfilter, {
            validate: ctx.debug,
        } );
    }

    if ( ctx.map.getSource( Municipality ) ) {
        removeLayersForSource( ctx.map, Municipality );
        ctx.map.removeSource( Municipality );
    }

    if ( ctx.map.getSource( Neighbourhood ) ) {
        removeLayersForSource( ctx.map, Neighbourhood );
        ctx.map.removeSource( Neighbourhood );
    }

    if ( ctx.map.getSource( District ) ) {
        removeLayersForSource( ctx.map, District );
        ctx.map.removeSource( District );
    }

    if ( ctx.map.getSource( Entity ) ) {
        removeLayersForSource( ctx.map, Entity );
        ctx.map.removeSource( Entity );
    }

    if ( ctx.map.getSource( Property ) ) {
        removeLayersForSource( ctx.map, Property );
        ctx.map.removeSource( Property );
    }

    const features = ctx.map.querySourceFeatures( Province, {
        filter: null,
        validate: ctx.debug,
    } );

    for ( let i = 0; i < features.length; i++ ) {
        ctx.map.setFeatureState(
            {
                source: Province,
                id: features[ i ].id,
            },
            {
                active: false,
            } );
    }

    let featureTypeToLoad = getNextChildLocationType( mapFeature );

    const url = new URL( '/' + featureTypeToLoad, ctx.apiURL );

    let featureParams = newFeatureFilter( {
        parentType: feature.type,
        parentID: feature.id,
    } );

    const hasIslands = feature.countOfChildType( Island );

    if ( hasIslands ) {
        if ( ctx.map.getLayer( `${Municipality}:fill` ) ) {
            ctx.map.setLayoutProperty( `${Municipality}:fill`, 'visibility', 'none', ctx.debug );
        }

        if ( ctx.map.getLayer( `${Municipality}:outline` ) ) {
            ctx.map.setLayoutProperty( `${Municipality}:outline`, 'visibility', 'none', ctx.debug );
        }
    }

    if ( ctx.map.getLayer( `${Region}:fill` ) ) {
        ctx.map.removeLayer( `${Region}:fill` );
    }

    if ( ctx.map.getLayer( `${Region}:outline` ) ) {
        ctx.map.removeLayer( `${Region}:outline` );
    }

    const propertyParams = newPropertyFilter( {}, ctx.baseForm );
    propertyParams.set( 'currentLocationID', feature.id );
    propertyParams.set( 'currentLocationType', Province );

    propertyParams.delete( 'longitude' );
    propertyParams.delete( 'latitude' );

    let options = {
        fetchFeatures: true,
        featureSearchURL: url,
        featureSearchParams: featureParams,
        fetchProperties: true,
        propertySearchURL: ctx.propertySearchURL,
        propertySearchParams: propertyParams,
        expectProperties: false,
    };

    let metaOptions = {
        focusMapToSearch: fitBounds,
        tasks: [],
        fetchPropertiesAfterAnimation: true,
    };

    let siblingOptions = () => {
        const bounds = ctx.map.getBounds();

        const siblingFeatureFilter = newFeatureFilter( {
            boundSW: bounds.getSouthWest().lat + ',' + bounds.getSouthWest().lng,
            boundNE: bounds.getNorthEast().lat + ',' + bounds.getNorthEast().lng,
        } );

        let excludeLocationIDs = [ feature.id ];

        const provinceSource = ctx.map.getSource( Province );

        if ( provinceSource ) {
            const provinceFeatures = provinceSource.serialize().data.features || [];
            excludeLocationIDs = [ ...excludeLocationIDs, provinceFeatures.map( ( f ) => parseInt( f.id ) ) ];
        }

        siblingFeatureFilter.set( 'excludeLocationIDs', excludeLocationIDs );

        const siblingOptions = {
            fetchFeatures: true,
            featureSearchURL: new URL( `/${Province}`, ctx.apiURL ),
            featureSearchParams: siblingFeatureFilter,
        };

        const siblingMeta = {
            focusMapToSearch: false,
            appendFeaturesIfLayerExists: true,
        };

        return {
            options: siblingOptions,
            meta: siblingMeta,
        };
    };

    metaOptions.tasks.push( wrapUpdateCall( ctx, siblingOptions ) );

    whenFeaturesAvailable( ctx.map, {
        source: featureTypeToLoad,
        filter: null,
        validate: ctx.debug,
    }, ctx.abortController.signal ).then( ( features ) => {
        for ( let i = 0; i < features.length; i++ ) {
            ctx.map.setFeatureState(
                {
                    source: featureTypeToLoad,
                    id: features[ i ].id,
                },
                {
                    active: true,
                } );
        }
    } )
        .catch( ( err ) => {
            if ( err.name === 'AbortError' ) {
                return;
            }
        } );

    ctx.movefuncs = [ ( ctx, feature ) => {
        if ( feature.source !== featureTypeToLoad ) {
            return;
        }

        const filter = {
            source: featureTypeToLoad,
            id: feature.id,
        };

        const state = {
            active: true,
        };

        ctx.map.setFeatureState( filter, state );
    } ];

    let results = [];

    try {
        results = await ctx.update( options, metaOptions, ctx.abortController.signal );
    } catch ( err ) {
        if ( err.name === 'AbortError' ) {
            return;
        }
    }

    if ( metaOptions.tasks.length > 0 ) {
        let tasks = [];

        for ( let i = 0; i < metaOptions.tasks.length; i++ ) {
            tasks.push( metaOptions.tasks[ i ] );
        }

        for ( let t = 0; t < tasks.length; t++ ) {
            const task = tasks[ t ];

            try {
                const result = await task();
                results = [ ...results, ...result ];
            } catch ( err ) {
                if ( err.name === 'AbortError' ) {
                    return;
                }
            }
        }
    }

    ctx.previousSearchState = propertyParams.toString();

    ctx.setLoading( false );
};

export {
    handleProvinceLayerClick,
};
